/* Thin adapter over @luce/core/auth TokenBridge. Keeps luce-fe's Apollo links
   and the refresh-retry OperationQueue; delegates all token state to the bridge. */

import { app } from "@/config/env";
import { storage } from "@/lib/storage";
import type { ApolloLink } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import type { ErrorLink, ErrorResponse } from "@apollo/client/link/error";
import { onError } from "@apollo/client/link/error";

import { tokenBridge } from "@luce/core/auth";

import type { QueuedRequest } from "./operation-queue";
import { OperationQueue } from "./operation-queue";

const REFRESH_TOKEN_KEY = "refreshToken";

tokenBridge.configure({
  refreshEndpoint: app.REFRESH_TOKEN_ENDPOINT,
  storage,
  refreshTokenKey: REFRESH_TOKEN_KEY,
});

type Tokens = {
  token: string;
  refreshToken: string;
};

class TokenManager {
  private queue = new OperationQueue();
  private fetching = false;

  enqueueRequest = (request: QueuedRequest) => this.queue.enqueueRequest(request);

  setTokens = ({ token, refreshToken }: Tokens) => {
    tokenBridge.setToken(token || null);
    void tokenBridge.setRefreshToken(refreshToken ?? "");
  };

  clearTokens = () => {
    tokenBridge.setToken(null);
    void tokenBridge.clearRefreshToken();
  };

  isTokenAvailable = async (): Promise<boolean> => {
    const jwt = await tokenBridge.refresh();
    return Boolean(jwt);
  };

  private handleFetchError = (error: unknown) => {
    console.log("token manager: error fetch token", error);
    this.queue.clearQueue();
    void tokenBridge.signOut();
  };

  errorHandler: ErrorLink.ErrorHandler = ({ graphQLErrors, operation, forward }: ErrorResponse) => {
    if (!graphQLErrors?.length) {
      return;
    }

    for (const error of graphQLErrors) {
      const { message } = error;

      if (message === "JWTExpired") {
        if (!this.fetching) {
          this.fetching = true;
          tokenBridge
            .refresh()
            .then((jwt) => {
              if (!jwt) {
                this.handleFetchError(new Error("refresh failed"));
                return;
              }
              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  Authorization: `Bearer ${jwt}`,
                },
              });
              this.queue.consumeQueue();
            })
            .finally(() => {
              this.fetching = false;
            });
        }
        return this.queue.enqueueRequest({ operation, forward });
      }

      if (message === "Invalid JWT" && !this.fetching) {
        void tokenBridge.signOut();
      }
    }
  };

  errorLink = (): ApolloLink => onError(this.errorHandler);

  authHeaderLink = () =>
    setContext((_, { headers }) => {
      const token = tokenBridge.getToken();
      return {
        headers: {
          ...headers,
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };
    });
}

export const tokenManager = new TokenManager();
